import { http } from './api';

export type DirectUploadResource = 'images' | 'videos' | 'sfx' | 'bgm';

export type PrepareResult = {
  existing: boolean;
  resource?: unknown;
  upload_id?: string;
  object_key?: string;
  put_url?: string;
  put_headers?: Record<string, string>;
  use_proxy?: boolean;
  claimed_checksum?: string;
};

/** 浏览器计算文件 SHA-256（小写 hex），仅作 Prepare 入参；权威值由后端 Complete 复核。 */
export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * 直传创建：prepare → PUT(对象存储或代理) → complete。
 * 若 checksum 已存在则直接返回已有资源（后端已合并 tags）。
 */
export async function directCreate<T>(
  resource: DirectUploadResource,
  file: File,
  values: Record<string, unknown>,
): Promise<T> {
  const checksum = await sha256Hex(file);
  const prep = (await http.post(`/admin/${resource}/upload/prepare`, {
    filename: file.name,
    size: file.size,
    mime_type: file.type || 'application/octet-stream',
    checksum,
    name: values.name,
    description: values.description,
    tags: values.tags,
    status: values.status,
    category: values.category,
  })) as PrepareResult;

  if (prep.existing) {
    return prep.resource as T;
  }
  if (!prep.upload_id || !prep.put_url) {
    throw new Error('直传准备失败：缺少 put_url');
  }

  const headers: Record<string, string> = { ...(prep.put_headers || {}) };
  if (!headers['Content-Type'] && file.type) {
    headers['Content-Type'] = file.type;
  }

  if (prep.use_proxy || prep.put_url.startsWith('/')) {
    await http.put(prep.put_url, file, {
      headers: {
        ...headers,
        'Content-Type': headers['Content-Type'] || 'application/octet-stream',
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  } else {
    const res = await fetch(prep.put_url, {
      method: 'PUT',
      headers,
      body: file,
    });
    if (!res.ok) {
      throw new Error(`对象存储上传失败: HTTP ${res.status}`);
    }
  }

  return (await http.post(`/admin/${resource}/upload/complete`, {
    upload_id: prep.upload_id,
    name: values.name,
    description: values.description,
    tags: values.tags,
    status: values.status,
    category: values.category,
  })) as T;
}
